import React from 'react';

import _isEmpty from 'lodash/isEmpty';
import { TranslateFunction } from 'react-localize-redux';

import { SelectOption } from '../../../components/atoms/Select';
import { Currency } from '../../../core/api/general/general.types';
import {
  CollectionOrder,
  DeliveryOrder,
} from '../../../core/api/order/order.types';
import {
  CollectionLocationArea,
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
  DeliveryType,
  PickupLocationOption,
  PickupLocationType,
  TableServiceCategory,
  TipsConfig,
} from '../../../core/api/restaurant/restaurant.types';
import { AppConfig } from '../../../core/api/whitelabel/whitelabel.types';
import { Basket, BasketSnapshot } from '../../../core/state/order/order.types';
import { Routes } from '../../../core/types/route.types';
import { getBasketItemTotal, getBasketTotal } from '../../../utils/basketUtils';
import { replaceCollectionWithPickup } from '../../../utils/localisationUtils';
import * as Api from '../../../weborder/Api';
import * as BasketControl from '../../../weborder/BasketControl';
import { BLANK } from '../../../weborder/constants/Constants';
import * as Data from '../../../weborder/Data';
import { OrderPayload } from '../../../weborder/selectors/basketSelectors';
import {
  BASKET_STORAGE_KEY,
  storage,
} from '../../../weborder/services/storageServices';
import { ChefNotes } from '../../../weborder/Store';

export type ResolveredAppConfig = {
  data: AppConfig;
};

export type CheckoutType = 'collection' | 'delivery';

type Order = {
  currency: Currency;
  order: DeliveryOrder | CollectionOrder;
  totalToPay: number;
  voucher: string;
};

export type ServiceTypes = {
  isDelivery: boolean;
  isDineInAndTakeOut: boolean;
  isDriveThrough: boolean;
  isDriveThroughOnly: boolean;
  isTableServiceAndTakeOut: boolean;
  isTableServiceOnly: boolean;
};

type RestaurantLocationMeta = {
  allowPreOrdersAndTableService: boolean;
  isTableServiceOnly: boolean;
  preOrderAvailable: boolean;
  serviceTypes: ServiceTypes;
  tableServiceCategory: TableServiceCategory;
};

type ChefNote = {
  type: string;
};

type BasketItem = {
  count: number;
  hasUpdate?: 'deleted' | 'price_change' | 'not_available';
  itemId: number;
  options: number[];
  order: number;
};

type BasketList = {
  [key: string]: {
    occurence?: number;
    index?: number;
    basketItem?: BasketControl.Item;
    name?: string;
    options?: string;
    price?: number;
  };
};

const calculateItemCounts = (basket: BasketControl.Data): BasketList => {
  const counts: any = {};

  for (let i = 0; i < basket.items.length; i += 1) {
    const item = basket.items[i];
    const key = item.Item.Name + item.Options.map((o) => o.Name).join(', ');
    if (counts[key]) {
      counts[key].quantity = counts[key].quantity
        ? counts[key].quantity + 1
        : 1;
      counts[key].index = i;
      counts[key].item = item;
    } else {
      counts[key] = { quantity: 1, index: i, item };
    }
  }
  return counts;
};

const computeBasketTaxes = (basket: BasketControl.Data): number => {
  let basketTaxes = 0;

  basket.items.forEach((item) => {
    basketTaxes += item.Item.TaxValue;

    item.Options.forEach((option) => {
      basketTaxes += option.TaxValue;
    });
  });

  return basketTaxes;
};

// For mapping time keys to time values
const preorderInfoText = (type: DeliveryType): string => {
  if (type === DeliveryType.Delivery) {
    return '-Select Delivery Time-';
  }

  return '-Select Collection Time-';
};

const computeDeliveryFee = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): number => {
  if (!restaurant) {
    return 0;
  }

  switch (restaurant.DeliveryType) {
    case DeliveryType.Delivery:
      return restaurant.DeliveryFee;

    default:
      return 0;
  }
};

const getFees = (total: number, fixedFee = 0, percentageFee = 0): number => {
  const fee = fixedFee + (total * percentageFee) / 100;

  return fee;
};

const computePurchaseFee = (
  account: Api.PaymentAccount,
  basket: BasketControl.Data,
  pickupLocationType: PickupLocationType,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): number => {
  if (account) {
    const paymentAccountIsCashFee =
      account.PaymentAccountType === 'CashPaymentAccount' ||
      account.PaymentAccountType === Api.GatewayPaymentAccount.Ideal ||
      account.PaymentAccountType === Api.GatewayPaymentAccount.Bancontact;

    /*
     * PaymentOptionType is when you click on New Card or New Paypal account
     * There is no PaymentAccount associated with these options but
     * They still should have a card fee added to totalToPay as payment goes through.
     */

    const shouldUseCashFee = paymentAccountIsCashFee;

    if (!restaurant) {
      return 0;
    }

    const isTableService =
      restaurant.DeliveryType === DeliveryType.Collection &&
      (restaurant.TableServiceCatagory === TableServiceCategory.Generic ||
        restaurant.TableServiceCatagory === TableServiceCategory.Table);

    const isTakeOutSelected = pickupLocationType === 0;
    const basketTotal = getBasketTotal(basket.items);

    if (shouldUseCashFee) {
      if (isTableService && !isTakeOutSelected) {
        return getFees(
          basketTotal,
          restaurant?.LocationServiceFixedFeeCashOrdersChargedToCustomerIncludingVat,
          restaurant?.LocationServicePercentFeeCashOrdersChargedToCustomerIncludingVat,
        );
      }

      return getFees(
        basketTotal,
        restaurant?.FixedFeeCashOrdersChargedToCustomerIncludingVat,
        restaurant?.PercentFeeCashOrdersChargedToCustomerIncludingVat,
      );
    }

    if (isTableService && !isTakeOutSelected) {
      return getFees(
        basketTotal,
        restaurant?.LocationServiceFixedFeeCardOrdersChargedToCustomerIncludingVat,
        restaurant?.LocationServicePercentFeeCardOrdersChargedToCustomerIncludingVat,
      );
    }

    return getFees(
      basketTotal,
      restaurant?.FixedFeeCardOrdersChargedToCustomerIncludingVat,
      restaurant?.PercentFeeCardOrdersChargedToCustomerIncludingVat,
    );
  }

  return 0;
};

const computeTaxes = (
  basket: BasketControl.Data,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): number => {
  const basketTaxes = computeBasketTaxes(basket);
  return (restaurant.DeliveryFeeTaxAmount || 0) + basketTaxes;
};

const computeServiceCharge = (
  basket: BasketControl.Data,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): number => {
  if (!restaurant.ServiceChargeConfiguration) return 0;

  const { IsEnabled, DisplayPercentage } =
    restaurant.ServiceChargeConfiguration;

  if (!IsEnabled) return 0;

  const basketTotal = getBasketTotal(basket.items);

  return (DisplayPercentage * basketTotal) / 100;
};

const getChefNote = (
  chefNote: ChefNote,
  chefNotes: ChefNotes,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
) => {
  if (chefNotes && ['custom', 'loaded'].includes(chefNote?.type)) {
    return chefNotes;
  }
  return restaurant?.ChefNote || '';
};

const getErrorMessage = (paymentError: string): string => {
  switch (paymentError) {
    case 'AuthenticationFailure':
      return 'Card_verification_failed_Please_try_again';
    case 'PaymentFailure':
    case 'ProcessingError':
      return 'Payment_failed_Please_try_again';
    case 'InsufficientFunds':
      return 'Your_card_has_insufficient_funds';
    case 'CardDeclined':
      return 'Card_declined_Please_use_another_card';
    case 'ExpiredCard':
      return 'Card_expired_Please_use_another_card';
    case 'IncorrectCvc':
      return 'Incorrect_cvc_Please_add_payment_method_again';
    default:
      return 'Incorrect_card_details_Please_try_again';
  }
};

const getItemTotalPrices = (basketItems: BasketList): number[] => {
  const basketItemPrices = Object.values(basketItems).map((item: any) =>
    getBasketItemTotal(item.item),
  );
  return basketItemPrices;
};

const getOrder = (
  basket: BasketControl.Data,
  orderPayload: OrderPayload,
  orderTotalToPay: number,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): Order => {
  const currency = (restaurant && restaurant.Currency) || Currency.EUR;

  if (!orderPayload || typeof orderPayload === 'string') {
    return null;
  }

  const v = basket.voucher;
  const voucher = v.status === 'valid' ? v.code : '';

  return {
    currency,
    order: orderPayload,
    totalToPay: orderTotalToPay,
    voucher,
  };
};

const getRestaurantUrl = (params: Routes['params']): string => {
  const { concessionStoreId, id, type, vrid } = params;
  const concessionId = concessionStoreId ? `/${concessionStoreId}` : '';
  const restaurant = `/restaurant/${vrid}/${type}/${id}${concessionId}`;
  return restaurant;
};

const isTipablePaymentMethod = (account: Api.PaymentAccount): boolean =>
  account && account.SupportsSubmitOrderTips;

const isTipEnabled = (
  appConfig: AppConfig,
  customer: Data.Async<Api.Customer>,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): boolean => {
  if (customer.status !== 'success' || !restaurant) {
    return false;
  }

  return appConfig.TipsEnabled && restaurant.TipsEnabled;
};

const basketHasServiceCharge = (basket: BasketControl.Data): boolean =>
  basket.serviceCharge.status !== 'none' && !!basket.serviceCharge.config;

const basketHasVoucher = (basket: BasketControl.Data): boolean =>
  basket.voucher.status !== 'none' && !!basket.voucher.items;

const showProcessingFee = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): boolean => {
  if (
    !restaurant.ServiceChargeConfiguration ||
    !restaurant.ServiceChargeConfiguration.IsEnabled
  )
    return true;

  return !restaurant.ServiceChargeConfiguration.DisplayAsOneWithProcessingFee;
};

const getIsDriveThrough = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): boolean =>
  restaurant.DeliveryType === DeliveryType.Collection &&
  restaurant.TableServiceCatagory === TableServiceCategory.DriveThrough;

const getTableServiceCategory = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): TableServiceCategory =>
  restaurant.DeliveryType === DeliveryType.Collection
    ? restaurant.TableServiceCatagory || TableServiceCategory.Generic
    : TableServiceCategory.Generic;

const getTableServiceIndex = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): number => {
  if (restaurant.DeliveryType !== DeliveryType.Collection) {
    return -1;
  }

  return restaurant.PickupLocationTypes.findIndex(
    (PickupLocation) => PickupLocation === PickupLocationType.TableService,
  );
};

const getDineInIndex = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): number => {
  if (restaurant.DeliveryType !== DeliveryType.Collection) {
    return -1;
  }

  return restaurant.PickupLocationTypes.findIndex(
    (PickupLocation) => PickupLocation === PickupLocationType.DineIn,
  );
};

const getTakeOutIndex = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): number =>
  restaurant.DeliveryType === DeliveryType.Collection &&
  restaurant.PickupLocationTypes.findIndex(
    (PickupLocation) => PickupLocation === PickupLocationType.TakeOut,
  );

const getIsTableServiceOnly = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  tableServiceIndex: number,
): boolean =>
  restaurant.DeliveryType === DeliveryType.Collection &&
  restaurant.PickupLocationTypes.length === 1 &&
  tableServiceIndex !== -1;

const getIsDriveThroughOnly = (
  restaurant: CollectionRestaurantDetail,
  tableServiceIndex: number,
): boolean =>
  getIsTableServiceOnly(restaurant, tableServiceIndex) &&
  restaurant.TableServiceCatagory === TableServiceCategory.DriveThrough;

const getIsTableSelectOnly = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  pickupLocationType: PickupLocationType,
): boolean =>
  !getIsDriveThrough(restaurant) &&
  pickupLocationType === PickupLocationType.TableService;

const getAllowPreordersAndTableServices = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): boolean =>
  restaurant.DeliveryType === DeliveryType.Collection
    ? restaurant.AllowPreOrdersAndTableService || true
    : true;

const getNameTitle = (
  hasRequestFullNameFeatureFlag: boolean,
  type: 'collection' | 'delivery',
  isTableServiceOnly: boolean,
  translate: TranslateFunction,
): string => {
  const yourNameTitle = hasRequestFullNameFeatureFlag
    ? translate('OrderCheckout_NameFieldLabel_YourFullNameOnOrder')
    : translate('OrderCheckout_NameFieldLabel_YourNameOnOrder');

  let orderTypeNote = '';
  if (type === 'delivery') {
    orderTypeNote = `(${translate(
      'OrderCheckout_NameFieldLabel_DeliveryNote',
    )})`;
  } else if (!isTableServiceOnly) {
    orderTypeNote = `(${translate(
      'OrderCheckout_NameFieldLabel_CollectionNote',
    )})`;
  }

  return `${yourNameTitle} ${orderTypeNote}`;
};

const getTableServiceMessage = (
  tableServiceCategory: TableServiceCategory,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  translate: TranslateFunction,
): string => {
  switch (tableServiceCategory) {
    case TableServiceCategory.Generic:
      return translate('Order_from_checkout', {
        restaurantName: restaurant.RestaurantName,
      }) as string;
    case TableServiceCategory.Villa:
      return translate('Villa_service_from_checkout', {
        restaurantName: restaurant.RestaurantName,
      }) as string;
    case TableServiceCategory.House:
      return translate('House_service_from_checkout', {
        restaurantName: restaurant.RestaurantName,
      }) as string;
    case TableServiceCategory.Room:
      return translate('Room_service_from_checkout', {
        restaurantName: restaurant.RestaurantName,
      }) as string;
    case TableServiceCategory.Area:
      return translate('Area_service_from_checkout', {
        restaurantName: restaurant.RestaurantName,
      }) as string;
    case TableServiceCategory.Table:
      return translate('Table_service_from_checkout', {
        restaurantName: restaurant.RestaurantName,
      }) as string;
    case TableServiceCategory.ParkingBay:
      return translate('Parking_bay_service_from_checkout', {
        restaurantName: restaurant.RestaurantName,
      }) as string;
    case TableServiceCategory.Gate:
      return translate('Gate_service_from_checkout', {
        restaurantName: restaurant.RestaurantName,
      }) as string;
    case TableServiceCategory.DriveThrough:
      return translate('Drive_Through_from_checkout', {
        restaurantName: restaurant.RestaurantName,
      }) as string;
    default:
      return translate('Order_from_checkout', {
        restaurantName: restaurant.RestaurantName,
      }) as string;
  }
};

const getPreorderAvailable = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): boolean => !restaurant.IsOpen && restaurant.AcceptPreOrders;

const getIsDisallowedPreorderAndTableService = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  pickupLocationType: PickupLocationType,
): boolean => {
  const preOrderAvailable = checkoutUtils.getPreorderAvailable(restaurant);
  const allowPreOrdersAndTableService =
    checkoutUtils.getAllowPreordersAndTableServices(restaurant);
  return (
    preOrderAvailable &&
    !allowPreOrdersAndTableService &&
    pickupLocationType === PickupLocationType.TableService
  );
};

export const getSelectDefaultOption = (
  countryCode: string,
  isTableServiceOnly: boolean,
  tableServiceCategory: TableServiceCategory,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  translate: TranslateFunction,
  isDineInOnly: boolean,
  isDineInAndTakeOut: boolean,
) => {
  if (isTableServiceOnly) {
    return getTableServiceMessage(tableServiceCategory, restaurant, translate);
  }
  if (isDineInOnly) {
    return translate('Select_time_to_dine_in');
  }
  if (isDineInAndTakeOut) {
    return replaceCollectionWithPickup(
      translate('Select_collection_dinein_time') as string,
      countryCode,
    );
  }
  return replaceCollectionWithPickup(
    translate('Select_collection_time') as string,
    countryCode,
  );
};

const getSelectOptionList = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  configData: AppConfig,
  pickupLocationType: PickupLocationType,
  translate: TranslateFunction,
): React.ReactNode => {
  const tableServiceCategory = getTableServiceCategory(restaurant);

  const tableServiceIndex = getTableServiceIndex(restaurant);

  const isTableServiceOnly = getIsTableServiceOnly(
    restaurant,
    tableServiceIndex,
  );

  const isDineInOnly = configData.DineInEnabled && !configData.PickupEnabled;
  const isDineInAndTakeOut =
    configData.DineInEnabled && configData.PickupEnabled;

  const collectionTimeMessage = getSelectDefaultOption(
    configData.CountryCode,
    isTableServiceOnly,
    tableServiceCategory,
    restaurant,
    translate,
    isDineInOnly,
    isDineInAndTakeOut,
  );

  const blankText =
    preorderInfoText(restaurant.DeliveryType) === '-Select Delivery Time-'
      ? `-${translate('Select_delivery_time')}-`
      : `-${collectionTimeMessage}-`;

  let extraOption = (
    <option key={BLANK} value={BLANK} disabled>
      {blankText}
    </option>
  );

  // if restaurant does not allow simultaneous preorder and table service then have the time option changed
  if (getIsDisallowedPreorderAndTableService(restaurant, pickupLocationType)) {
    extraOption = (
      <option key={BLANK} value={BLANK}>
        -----------------------
      </option>
    );
  }

  const optionList = restaurant.PreOrderOptions.PreOrderTimes.map((t) => (
    <option disabled={t.IsFull} key={t.Key} value={t.Key}>
      {t.IsFull ? `${t.Value} (${translate('Full')})` : t.Value}
    </option>
  ));

  // Concatenate info value to the dropdown list
  optionList.unshift(extraOption);
  return optionList;
};

const getSelectTimeOptions = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  configData: AppConfig,
  pickupLocationType: any,
  translate: TranslateFunction,
): SelectOption[] => {
  const tableServiceIndex = checkoutUtils.getTableServiceIndex(restaurant);
  const tableServiceCategory =
    checkoutUtils.getTableServiceCategory(restaurant);

  const isTableServiceOnly = checkoutUtils.getIsTableServiceOnly(
    restaurant,
    tableServiceIndex,
  );
  const isDineInOnly = configData.DineInEnabled && !configData.PickupEnabled;
  const isDineInAndTakeOut =
    configData.DineInEnabled && configData.PickupEnabled;
  const isTableServiceAndNoPreorder =
    checkoutUtils.getIsDisallowedPreorderAndTableService(
      restaurant,
      pickupLocationType,
    );

  const collectionTimeMessage = checkoutUtils.getSelectDefaultOption(
    configData.CountryCode,
    isTableServiceOnly,
    tableServiceCategory,
    restaurant,
    translate,
    isDineInOnly,
    isDineInAndTakeOut,
  );

  const defaultOptionMessage =
    preorderInfoText(restaurant.DeliveryType) === '-Select Delivery Time-'
      ? `-${translate('Select_delivery_time')}-`
      : `-${collectionTimeMessage}-`;

  const defaultOptions = [];
  defaultOptions.push({
    disabled: true,
    label: defaultOptionMessage,
    key: BLANK,
  });

  if (isTableServiceAndNoPreorder) {
    defaultOptions.push({
      disabled: true,
      label: '-----------------------',
      key: BLANK,
    });
  }

  const optionList = restaurant.PreOrderOptions.PreOrderTimes.map(
    (t): SelectOption => ({
      disabled: t.IsFull,
      label: t.IsFull ? `${t.Value} (${translate('Full')})` : t.Value,
      key: t.Key,
    }),
  );

  const allOptions = defaultOptions.concat(optionList);
  return allOptions;
};

// We need to force the 'RequestedDeliveryOrPickupTimeUtc' time to not be null.
// Set it to the first item in the option list.
const forcePreOrderSelection = (
  changeDeliveryTime: (time: string) => any,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  requestedDeliveryOrPickupTimeUtc,
  pickupLocationType: PickupLocationType,
  setStoredTime: (time: string) => any,
): void => {
  if (!restaurant) {
    return;
  }
  const preOrderAvailable = getPreorderAvailable(restaurant);

  const allowPreOrdersAndTableService =
    getAllowPreordersAndTableServices(restaurant);

  // if restaurant does not allow simultaneous preorder & table service then switch the time dropdown
  if (
    preOrderAvailable &&
    !allowPreOrdersAndTableService &&
    pickupLocationType === PickupLocationType.TableService &&
    (requestedDeliveryOrPickupTimeUtc !== BLANK ||
      !requestedDeliveryOrPickupTimeUtc)
  ) {
    setStoredTime(requestedDeliveryOrPickupTimeUtc);
    return;
  }

  if (requestedDeliveryOrPickupTimeUtc) {
    return;
  }
  const { PreOrderOptions } = restaurant;

  if (!PreOrderOptions) {
    return;
  }
  if (PreOrderOptions.PreOrderTimes.length < 1) {
    return;
  }

  const [defaultTime] = PreOrderOptions.PreOrderTimes;

  // Important!
  // Don't dispatch an update to Redux that will trigger componentWillReceiveProps()
  // from inside componentWillReceiveProps u0nless we need to. Without this
  // guard React will recurse until the stack is exceeded, and the app crashes.
  if (defaultTime.Key === requestedDeliveryOrPickupTimeUtc) {
    return;
  }

  if (!PreOrderOptions.RequireExplicitSelect) {
    changeDeliveryTime(defaultTime.Key);
  } else {
    changeDeliveryTime(BLANK);
  }
};

const getCategoryNoteTranslation = (Category: TableServiceCategory): string => {
  let categoryNoteTranslation = '';
  switch (Category) {
    case TableServiceCategory.Villa:
      categoryNoteTranslation = 'Barista';
      break;
    case TableServiceCategory.House:
      categoryNoteTranslation = 'Store_lowercase';
      break;
    default:
      categoryNoteTranslation = 'Chef';
  }
  return categoryNoteTranslation;
};

const getPaymentTypeLabel = (
  account: Api.PaymentAccount,
  translate: TranslateFunction,
): string => {
  switch (account.PaymentAccountType) {
    case 'CardPaymentAccount':
      if (account.Description === 'New_card') {
        return `${translate('New_card')}`;
      }
      // Example of existing card description "Card ****4242"
      return account.Description;

    case 'StripePayButtonPaymentAccount':
      if (account.Description === 'Apple_pay') {
        return `${translate('Apple_pay')}`;
      }
      return `${translate('Google_microsoft_pay')}`;

    case 'PayGreenRestaurantVoucher':
      return `${translate('RestaurantVoucher_label')}`;

    default:
      // Other payment types have a suitable translated label from Backend
      return account.Description;
  }
};

/* BasketItems it is not an array but it is assumed that it is on the stoer */
const getBasketSnapshot = (
  account: Api.PaymentAccount,
  basket: BasketControl.Data,
  currentLang: string,
  orderPayload: OrderPayload,
  orderTotalToPay: number,
  percentageTip: number,
  pickupLocationType: PickupLocationType,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  translate: TranslateFunction,
): BasketSnapshot => {
  const total: Order = checkoutUtils.getOrder(
    basket,
    orderPayload,
    orderTotalToPay,
    restaurant,
  );
  const basketItems = checkoutUtils.calculateItemCounts(basket);
  const basketItemPrices = checkoutUtils.getItemTotalPrices(basketItems);
  const basketSnapshot = {
    basketItemPrices,
    basketItems: basketItems as unknown as BasketItem[],
    deliveryFee: checkoutUtils.computeDeliveryFee(restaurant),
    displayTax: restaurant.DisplayTax,
    excludeServiceCharge: basket.excludeServiceCharge,
    initialLang: currentLang,
    paymentType: checkoutUtils.getPaymentTypeLabel(account, translate),
    percentageTip,
    physicalRestaurantAddress: restaurant.PhysicalRestaurantAddress,
    processingFee: checkoutUtils.computePurchaseFee(
      account,
      basket,
      pickupLocationType,
      restaurant,
    ),
    processingFeeTaxRate: restaurant.ProcessingFeeTaxRate,
    serviceCharge: basket.serviceCharge,
    taxes: checkoutUtils.computeTaxes(basket, restaurant),
    total,
    voucher: basket.voucher,
  };

  return basketSnapshot;
};

const getErrorFromUrl = (routeParams: any): string => {
  if (routeParams && routeParams.location.search) {
    const urlParams = new URLSearchParams(routeParams?.location?.search);
    const paymentError = urlParams.get('paymentError') as string;
    if (paymentError) {
      const errorMessage = checkoutUtils.getErrorMessage(paymentError);
      const error = {
        name: 'Payment_Error',
        message: errorMessage,
      } as any; // Error;
      return error;
    }
  }
  return null;
};

const getRestaurantLocationMeta = (
  restaurant: CollectionRestaurantDetail,
  type: string,
): RestaurantLocationMeta => {
  const tableServiceIndex = getTableServiceIndex(restaurant);

  const dineInIndex = getDineInIndex(restaurant);

  const takeOutIndex = getTakeOutIndex(restaurant);

  const isTableServiceOnly = getIsTableServiceOnly(
    restaurant,
    tableServiceIndex,
  );

  const isDineInAndTakeOut = dineInIndex !== -1 && takeOutIndex !== -1;

  const isTableServiceAndTakeOut =
    tableServiceIndex !== -1 && takeOutIndex !== -1;

  const isDriveThrough = getIsDriveThrough(restaurant);

  const isDriveThroughOnly = getIsDriveThroughOnly(
    restaurant,
    tableServiceIndex,
  );

  const serviceTypes = {
    isDelivery: type === 'delivery',
    isDriveThrough,
    isDriveThroughOnly,
    isTableServiceOnly,
    isDineInAndTakeOut,
    isTableServiceAndTakeOut,
  };

  const tableServiceCategory = getTableServiceCategory(restaurant);

  const preOrderAvailable = getPreorderAvailable(restaurant);

  const allowPreOrdersAndTableService =
    getAllowPreordersAndTableServices(restaurant);

  return {
    serviceTypes,
    tableServiceCategory,
    preOrderAvailable,
    allowPreOrdersAndTableService,
    isTableServiceOnly,
  };
};

const getTippingRates = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): number[] =>
  restaurant?.TipsConfig?.Items
    ? restaurant.TipsConfig.Items.map((rate) =>
        Math.round(rate.Percentage * 100),
      )
    : [];

const isCustomTipEnabled = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): boolean => restaurant?.TipsConfig?.AllowCustomTip;

const areEmojisEnabled = (
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
): boolean => restaurant?.TipsConfig?.AllowEmojis;

const getTableListOptions = (
  locationAreas: CollectionLocationArea[],
  pickupLocationOptions: PickupLocationOption[],
): SelectOption[] => {
  if (!_isEmpty(locationAreas)) {
    const locationAreaoptions = locationAreas.reduce((acc, area) => {
      const optionHeader = {
        disabled: true,
        key: area.LocationAreaName,
        label: area.LocationAreaName,
      };
      const optionsArray = area.Locations.map((location) => ({
        disabled: false,
        key: location.LocationId.toString(),
        label: location.LocationName,
      }));
      return acc.concat(optionHeader, optionsArray);
    }, []);

    return locationAreaoptions;
  }
  if (!_isEmpty(pickupLocationOptions)) {
    const pickupOptions = pickupLocationOptions
      .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
      .map(
        (location): SelectOption => ({
          disabled: location.Value.toString().startsWith('---'),
          key: location.PickupLocationOptionId.toString(),
          label: location.Value.toString(),
        }),
      );

    return pickupOptions;
  }

  return [];
};

export const clearBasketAndMenuLocalStorage = (
  menuId: number,
  menuUrl: string,
): void => {
  const basketStorageKey = BASKET_STORAGE_KEY(menuId);
  storage.removeItem(menuUrl);
  storage.removeItem(basketStorageKey);
};

export const getRestaurantTipValue = (tipsConfig): number | undefined => {
  const defaultTipRate = tipsConfig?.Items
    ? tipsConfig.Items.find((tip) => tip.IsDefault)
    : undefined;

  return defaultTipRate
    ? Math.round(defaultTipRate.Percentage * 100)
    : undefined;
};

export const getDefaultTipSetting = (
  tipsConfig: TipsConfig,
  basket: Basket,
): number | undefined => {
  const restaurantTip = getRestaurantTipValue(tipsConfig);

  return basket && typeof basket.tip === 'number' ? basket.tip : restaurantTip;
};

export const checkoutUtils = {
  areEmojisEnabled,
  basketHasServiceCharge,
  basketHasVoucher,
  calculateItemCounts,
  computeBasketTaxes,
  computeDeliveryFee,
  computePurchaseFee,
  computeServiceCharge,
  computeTaxes,
  forcePreOrderSelection,
  getAllowPreordersAndTableServices,
  getBasketSnapshot,
  getCategoryNoteTranslation,
  getChefNote,
  getDineInIndex,
  getErrorFromUrl,
  getErrorMessage,
  getIsDisallowedPreorderAndTableService,
  getIsDriveThrough,
  getIsDriveThroughOnly,
  getIsTableSelectOnly,
  getIsTableServiceOnly,
  getItemTotalPrices,
  getNameTitle,
  getOrder,
  getPaymentTypeLabel,
  getPreorderAvailable,
  getRestaurantLocationMeta,
  getRestaurantUrl,
  getSelectDefaultOption,
  getSelectOptionList,
  getSelectTimeOptions,
  getTableListOptions,
  getTableServiceCategory,
  getTableServiceIndex,
  getTableServiceMessage,
  getTakeOutIndex,
  getTippingRates,
  isCustomTipEnabled,
  isTipablePaymentMethod,
  isTipEnabled,
  showProcessingFee,
};
